import React from 'react';
import { Input, Select } from 'antd';

interface Page2TabProps {
  formData: Record<string, unknown>;
  isEditing: boolean;
  isApproved: boolean;
  getField: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
}

const FACE_BOTTOM_OPTIONS = [
  { value: 0, label: '（未選）' },
  { value: 1, label: '面紗' },
  { value: 2, label: '底紗' },
];

const TYPE_OPTIONS = [
  { value: 0, label: '（未選）' },
  { value: 1, label: 'SD' },
  { value: 2, label: 'FD' },
  { value: 3, label: 'Yarn-dyed' },
  { value: 4, label: '異形斷面' },
  { value: 5, label: 'Bright' },
  { value: 6, label: 'Trilobal' },
];

const RAW_PROCESS_OPTIONS = [
  { value: 0, label: '（未選）' },
  { value: 1, label: '原絲' },
  { value: 2, label: '加工絲' },
];

/** Render one yarn layer row (face/bottom/3rd). */
const YarnLayerRow: React.FC<{
  label: string;
  prefix: string; // e.g. 'fj740_20' for layer 1, 'fj740_21' for layer 2
  disabled: boolean;
  getField: (key: string) => unknown;
  setField: (key: string, value: unknown) => void;
}> = ({ label, prefix, disabled, getField, setField }) => {
  const str = (suffix: string) => (getField(`${prefix}${suffix}`) as string) || '';
  const num = (suffix: string) => (getField(`${prefix}${suffix}`) as number) || 0;
  // field suffixes: 1=面底, 2=成份, 3=D/f's, 4=種類, 5=原加, 6=成本, 7=單位, 8=供應商

  return (
    <tr className="j219-yarn-row">
      <td className="j219-yarn-label">{label}</td>
      <td>
        <Select
          data-contract-field={`${prefix}1`}
          value={num('1')}
          onChange={v => setField(`${prefix}1`, v)}
          disabled={disabled}
          options={FACE_BOTTOM_OPTIONS}
          style={{ width: 80 }}
          size="small"
        />
      </td>
      <td>
        <Input
          data-contract-field={`${prefix}2`}
          value={str('2')}
          onChange={e => setField(`${prefix}2`, e.target.value)}
          disabled={disabled}
          size="small"
          style={{ width: 80 }}
        />
      </td>
      <td>
        <Input
          data-contract-field={`${prefix}3`}
          value={str('3')}
          onChange={e => setField(`${prefix}3`, e.target.value)}
          disabled={disabled}
          size="small"
          style={{ width: 80 }}
        />
      </td>
      <td>
        <Select
          data-contract-field={`${prefix}4`}
          value={num('4')}
          onChange={v => setField(`${prefix}4`, v)}
          disabled={disabled}
          options={TYPE_OPTIONS}
          style={{ width: 110 }}
          size="small"
        />
      </td>
      <td>
        <Select
          data-contract-field={`${prefix}5`}
          value={num('5')}
          onChange={v => setField(`${prefix}5`, v)}
          disabled={disabled}
          options={RAW_PROCESS_OPTIONS}
          style={{ width: 90 }}
          size="small"
        />
      </td>
      <td>
        <Input
          data-contract-field={`${prefix}6`}
          type="number"
          value={num('6')}
          onChange={e => setField(`${prefix}6`, parseFloat(e.target.value) || 0)}
          disabled={disabled}
          size="small"
          style={{ width: 80 }}
        />
      </td>
      <td>
        <Input
          data-contract-field={`${prefix}7`}
          value={str('7')}
          onChange={e => setField(`${prefix}7`, e.target.value)}
          disabled={disabled}
          size="small"
          style={{ width: 60 }}
        />
      </td>
      <td>
        <Input
          data-contract-field={`${prefix}8`}
          value={str('8')}
          onChange={e => setField(`${prefix}8`, e.target.value)}
          disabled={disabled}
          size="small"
          style={{ width: 80 }}
        />
      </td>
    </tr>
  );
};

const Page2Tab: React.FC<Page2TabProps> = React.memo(({
  formData, isEditing, isApproved, getField, setField,
}) => {
  const disabled = !isEditing || isApproved;
  const str = (k: string): string => (getField(k) as string) || '';
  const num = (k: string): number => (getField(k) as number) || 0;

  return (
    <div className="j219-page-content">
      {/* Region: Shape2 — 紗線 */}
      <div className="j219-section-frame">
        <h4 className="j219-section-title">紗線</h4>
        <div className="j219-table-wrapper">
          <table className="j219-yarn-table">
            <thead>
              <tr>
                <th></th>
                <th>面/底</th>
                <th>成份</th>
                <th>D/f&apos;s</th>
                <th>種類</th>
                <th>原/加</th>
                <th>成本</th>
                <th>單位</th>
                <th>供應商</th>
              </tr>
            </thead>
            <tbody>
              <YarnLayerRow label="第1層" prefix="fj740_20" disabled={disabled} getField={getField} setField={setField} />
              <YarnLayerRow label="第2層" prefix="fj740_21" disabled={disabled} getField={getField} setField={setField} />
              <YarnLayerRow label="第3層" prefix="fj740_22" disabled={disabled} getField={getField} setField={setField} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Region: Shape1 — 織布工程 */}
      <div className="j219-section-frame">
        <h4 className="j219-section-title">織布工程</h4>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>織布廠</label>
          <Input value={str('fj740_41')} disabled={disabled} onChange={e => setField('fj740_41', e.target.value)} data-contract-field="fj740_41" />
        </div>
        <div className="j219-field">
          <label>織工(NTD)</label>
          <Input type="number" value={num('fj740_42')} disabled={disabled} onChange={e => setField('fj740_42', parseFloat(e.target.value) || 0)} data-contract-field="fj740_42" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>單位</label>
          <Input value={str('fj740_43')} disabled={disabled} onChange={e => setField('fj740_43', e.target.value)} data-contract-field="fj740_43" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>機台</label>
          <Input value={str('fj740_44')} disabled={disabled} onChange={e => setField('fj740_44', e.target.value)} data-contract-field="fj740_44" />
        </div>
        <div className="j219-field">
          <label>織縮(損耗)</label>
          <Input type="number" value={num('fj740_45')} disabled={disabled} onChange={e => setField('fj740_45', parseFloat(e.target.value) || 0)} data-contract-field="fj740_45" />
        </div>
      </div>
      </div>{/* close section-frame for 織布工程 */}

      {/* Region: Shape3 — 組織/密度 */}
      <div className="j219-section-frame">
      <h4 className="j219-section-title">組織/密度</h4>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>組織</label>
          <Input value={str('fj740_23')} disabled={disabled} onChange={e => setField('fj740_23', e.target.value)} data-contract-field="fj740_23" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>成份%</label>
          <Input type="number" value={num('fj740_251')} disabled={disabled} onChange={e => setField('fj740_251', parseInt(e.target.value) || 0)} data-contract-field="fj740_251" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>%</label>
          <Input type="number" value={num('fj740_252')} disabled={disabled} onChange={e => setField('fj740_252', parseInt(e.target.value) || 0)} data-contract-field="fj740_252" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>%</label>
          <Input type="number" value={num('fj740_253')} disabled={disabled} onChange={e => setField('fj740_253', parseInt(e.target.value) || 0)} data-contract-field="fj740_253" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>幅寬(內)</label>
          <Input type="number" value={num('fj740_241')} disabled={disabled} onChange={e => setField('fj740_241', parseFloat(e.target.value) || 0)} data-contract-field="fj740_241" />
        </div>
        <div className="j219-field">
          <label>幅寬(外)</label>
          <Input type="number" value={num('fj740_242')} disabled={disabled} onChange={e => setField('fj740_242', parseFloat(e.target.value) || 0)} data-contract-field="fj740_242" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>重量</label>
          <Input type="number" value={num('fj740_261')} disabled={disabled} onChange={e => setField('fj740_261', parseFloat(e.target.value) || 0)} data-contract-field="fj740_261" />
        </div>
        <div className="j219-field">
          <label>g/㎡</label>
          <Input type="number" value={num('fj740_262')} disabled={disabled} onChange={e => setField('fj740_262', parseFloat(e.target.value) || 0)} data-contract-field="fj740_262" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>毛圈長(mm)</label>
          <Input type="number" value={num('fj740_231')} disabled={disabled} onChange={e => setField('fj740_231', parseFloat(e.target.value) || 0)} data-contract-field="fj740_231" />
        </div>
        <div className="j219-field">
          <label>針數(G)</label>
          <Input type="number" value={num('fj740_232')} disabled={disabled} onChange={e => setField('fj740_232', parseInt(e.target.value) || 0)} data-contract-field="fj740_232" />
        </div>
        <div className="j219-field">
          <label>口數(F)</label>
          <Input type="number" value={num('fj740_233')} disabled={disabled} onChange={e => setField('fj740_233', parseInt(e.target.value) || 0)} data-contract-field="fj740_233" />
        </div>
        <div className="j219-field">
          <label>圓徑(inch)</label>
          <Input type="number" value={num('fj740_234')} disabled={disabled} onChange={e => setField('fj740_234', parseInt(e.target.value) || 0)} data-contract-field="fj740_234" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>成本(NTD)</label>
          <Input type="number" value={num('fj740_281')} disabled={disabled} onChange={e => setField('fj740_281', parseFloat(e.target.value) || 0)} data-contract-field="fj740_281" />
        </div>
        <div className="j219-field j219-field-sm">
          <label>單位</label>
          <Input value={str('fj740_282')} disabled={disabled} onChange={e => setField('fj740_282', e.target.value)} data-contract-field="fj740_282" />
        </div>
      </div>
      <div className="j219-form-row">
        <div className="j219-field">
          <label>供應商</label>
          <Input value={str('fj740_29')} disabled={disabled} onChange={e => setField('fj740_29', e.target.value)} data-contract-field="fj740_29" />
        </div>
        <div className="j219-field">
          <label>工廠布號</label>
          <Input value={str('fj740_291')} disabled={disabled} onChange={e => setField('fj740_291', e.target.value)} data-contract-field="fj740_291" />
        </div>
      </div>
      </div>{/* close section-frame for 組織/密度 */}
    </div>
  );
});

Page2Tab.displayName = 'Page2Tab';
export default Page2Tab;
